import { Caption } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';

import { BidConsoleOpener } from '@/components/BidConsoleOpener/BidConsoleOpener';
import CatalogDate from '@/components/CatalogDate/CatalogDate';
import { useAppSelector } from '@/redux/hooks';
import { getLiveCatalogCurrentItemIndex } from '@/redux/modules/console';
import {
    getItemSummaryBuyNowStatus,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIndex,
    getItemSummaryIsAvailable,
    getItemSummaryIsCatalogOnly,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummarySaleStartTs,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { shouldShowLive } from '@/utils/live';
import noop from '@/utils/noop';

export type LiveButtonProps = {
    allowBidding?: boolean;
    isCatalogPage?: boolean;
    isLiveButtonActive?: (setBoolean: boolean) => void;
    itemId: number;
    showDate?: boolean;
};

const ItemCardLiveButtonComponent = ({
    allowBidding,
    isCatalogPage,
    isLiveButtonActive = noop,
    itemId,
    showDate,
}: LiveButtonProps) => {
    const formatMessage = useAppFormatMessage();
    const buyNowStatus = useAppSelector((state) => getItemSummaryBuyNowStatus(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const index = useAppSelector((state) => getItemSummaryIndex(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isCatalogOnly = useAppSelector((state) => getItemSummaryIsCatalogOnly(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));

    const currentItemIndex = useAppSelector((state) => getLiveCatalogCurrentItemIndex(state, catalogId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));

    const showLiveButton = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
    const buyNowAvailable = buyNowStatus === 1 && catalogStatus === AuctionStatus.Done;
    const lotsAway = index - currentItemIndex;
    const isComingUp = isLotOpen || (currentItemIndex <= index && isAvailable && !isPassed && !isSold);

    if (buyNowAvailable) {
        return null;
    }

    if (showLiveButton && isComingUp) {
        return (
            <Caption className="block flex-[0_1_auto] smMin:text-sm smMin:leading-[21px]">
                <span className={cn('inline-block', isCatalogPage && 'pt-xs')}>
                    <BidConsoleOpener
                        catalogId={catalogId}
                        shouldTrackEnterLiveAuction={isCatalogPage}
                    >
                        <span
                            className="relative inline-block cursor-pointer pr-xs text-text-critical after:absolute after:right-0 after:top-[5px] after:h-0 after:w-0 after:border-b-[4px] after:border-l-[4px] after:border-t-[4px] after:border-b-transparent after:border-l-critical-500 after:border-t-transparent after:content-[''] hover:text-critical-400 hover:underline hover:after:border-l-critical-400 smMin:after:top-[7px]"
                            onMouseOut={() => isLiveButtonActive(false)}
                            onMouseOver={() => isLiveButtonActive(true)}
                        >
                            {lotsAway > 0 && allowBidding
                                ? lotsAway === 1
                                    ? formatMessage(['itemBidding', 'lotAway_short'])
                                    : formatMessage(['itemBidding', 'lotsAway_short'], { values: { lotsAway } })
                                : formatMessage(['bid_live'])}
                        </span>
                    </BidConsoleOpener>
                </span>
            </Caption>
        );
    }

    return (
        <Caption className="block flex-[0_1_auto] smMin:text-sm smMin:leading-[21px]">
            <CatalogDate
                date={saleStartTs}
                showDate={showDate}
            />
        </Caption>
    );
};

export const ItemCardLiveButton = memo<LiveButtonProps>(ItemCardLiveButtonComponent);
